import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { generateConductUrl } from '@/lib/utils';
import { Search, Filter, Mail, Video, Calendar, Clock, Smile, BarChart3, Thermometer, ExternalLink, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

type SessionStatus = 'scheduled' | 'completed' | 'cancelled' | 'missed' | 'rescheduled';

interface TeamMemberSummary {
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    email?: string;
  };
}

interface OneOnOneSetupSummary {
  id: string;
  title?: string;
  participantId: string;
  managerId: string;
  workflowType: 'email' | 'physical';
  isActive: boolean;
  duration: number;
  frequency: string;
  createdAt: string;
  nextScheduledDate?: string;
}

interface OneOnOneSessionSummary {
  id: string;
  setupId: string;
  teamId: string;
  managerId: string;
  participantId: string;
  title: string;
  scheduledDate: string;
  actualDate?: string;
  completedAt?: string;
  duration: number;
  status: SessionStatus;
  deliveryMethod: 'email' | 'physical';
  agenda?: string[];
  notes?: string;
  mood?: number;
  productivityRating?: number;
  teamTempRating?: number;
}

interface ViewOneOnOneSessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  setup: OneOnOneSetupSummary | null;
  sessions: OneOnOneSessionSummary[];
  manager?: TeamMemberSummary | null;
  participant?: TeamMemberSummary | null;
}

const statusConfig: Record<SessionStatus, { label: string; className: string }> = {
  completed: { label: 'completed', className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  scheduled: { label: 'scheduled', className: 'bg-sky-50 text-sky-700 border border-sky-200' },
  cancelled: { label: 'cancelled', className: 'bg-rose-50 text-rose-700 border border-rose-200' },
  missed: { label: 'missed', className: 'bg-amber-50 text-amber-700 border border-amber-200' },
  rescheduled: { label: 'rescheduled', className: 'bg-indigo-50 text-indigo-700 border border-indigo-200' },
};

function formatDate(dateIso?: string) {
  if (!dateIso) return 'Not recorded';
  const date = new Date(dateIso);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return `${date.toLocaleDateString(undefined, { dateStyle: 'medium' })}, ${date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}

function formatDuration(minutes: number) {
  if (!minutes) return '—';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs && mins) return `${hrs}h ${mins}m`;
  if (hrs) return `${hrs}h`;
  return `${mins} min`;
}

export function ViewOneOnOneSessionsModal({
  isOpen,
  onClose,
  setup,
  sessions,
  manager,
  participant,
}: ViewOneOnOneSessionsModalProps) {
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | SessionStatus>('all');
  const [page, setPage] = React.useState(1);
  const pageSize = 6;

  const filteredSessions = React.useMemo(() => {
    return sessions.filter((session) => {
      const matchesSearch = search.trim().length === 0 || session.title.toLowerCase().includes(search.trim().toLowerCase()) || (session.notes?.toLowerCase().includes(search.trim().toLowerCase()) ?? false);
      const matchesStatus = statusFilter === 'all' || session.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [sessions, search, statusFilter]);

  React.useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sessions]);

  const totalPages = Math.max(1, Math.ceil(filteredSessions.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = Math.min(filteredSessions.length, startIndex + pageSize);
  const paginatedSessions = filteredSessions.slice(startIndex, startIndex + pageSize);

  React.useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handlePrevPage = () => {
    setPage((current) => Math.max(1, current - 1));
  };

  const handleNextPage = () => {
    setPage((current) => Math.min(totalPages, current + 1));
  };

  const handleOpenConduct = (session: OneOnOneSessionSummary) => {
    const url = generateConductUrl({
      ...session,
      type: session.deliveryMethod === 'email' ? 'Email' : 'Physical Meeting',
    });
    window.open(url, '_blank');
  };

  const managerName = manager?.user?.fullName || `${manager?.user?.firstName ?? ''} ${manager?.user?.lastName ?? ''}`.trim() || 'Manager';
  const participantName = participant?.user?.fullName || `${participant?.user?.firstName ?? ''} ${participant?.user?.lastName ?? ''}`.trim() || 'Team member';
  const setupTitle = setup?.title?.trim().length ? setup.title : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle className="text-2xl font-bold text-foreground">
            Sessions for {managerName && participantName ? `${managerName} & ${participantName}` : '1-on-1'}
          </DialogTitle>
          <div className="text-sm text-muted-foreground mt-1 space-y-1">
            {setupTitle && <div>Setup: {setupTitle}</div>}
            <div>{sessions.length} total {sessions.length === 1 ? 'session' : 'sessions'}</div>
          </div>
        </DialogHeader>

        <div className="px-6 py-4 space-y-4 flex-1 overflow-hidden flex flex-col">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search sessions by title or notes"
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setStatusFilter('all')}
                data-state={statusFilter === 'all' ? 'active' : undefined}
              >
                <Filter className="h-4 w-4" />
                All Statuses
              </Button>
              {(['completed', 'scheduled', 'cancelled'] as SessionStatus[]).map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'ghost'}
                  size="sm"
                  className="capitalize"
                  onClick={() => setStatusFilter(status)}
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="flex-1 overflow-y-auto pr-4">
            <div className="space-y-4 pb-2">
              {paginatedSessions.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  No sessions match your filters yet.
                </div>
              ) : (
                paginatedSessions.map((session) => {
                  const config = statusConfig[session.status];
                  const scheduledDate = session.scheduledDate ? new Date(session.scheduledDate) : null;
                  const completedDate = session.completedAt ? new Date(session.completedAt) : null;
                  const isCompleted = session.status === 'completed' || !!completedDate;
                  const now = new Date();
                  const daysSinceScheduled = scheduledDate ? Math.floor((now.getTime() - scheduledDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
                  const overdueBadge = !isCompleted && scheduledDate && daysSinceScheduled !== null && daysSinceScheduled > 0;

                  return (
                    <div key={session.id} className="rounded-xl border border-border bg-card shadow-sm">
                      <div className="flex flex-col gap-4 p-5">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold text-foreground">{session.title}</h3>
                              <Badge className={cn('capitalize', config.className)}>{config.label}</Badge>
                              <Badge variant="outline" className="flex items-center gap-1">
                                {session.deliveryMethod === 'email' ? <Mail className="h-3 w-3" /> : <Video className="h-3 w-3" />}
                                {session.deliveryMethod === 'email' ? 'Email' : 'Physical'}
                              </Badge>
                            </div>
                            <div className="mt-2 flex flex-col gap-2 text-sm text-muted-foreground md:flex-row md:items-center md:gap-6">
                              <span className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                Scheduled: {formatDate(session.scheduledDate)}
                              </span>
                              {session.completedAt && (
                                <span className="flex items-center gap-2">
                                  <Clock className="h-4 w-4" />
                                  Completed: {formatDate(session.completedAt)}
                                </span>
                              )}
                              <span className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                Duration: {formatDuration(session.duration)}
                              </span>
                              {overdueBadge && (
                                <Badge variant="destructive" className="flex items-center gap-1 px-2 py-1">
                                  Overdue {daysSinceScheduled} {daysSinceScheduled === 1 ? 'day' : 'days'}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button variant="outline" size="sm" className="gap-2" onClick={() => handleOpenConduct(session)}>
                            <ExternalLink className="h-4 w-4" />
                            View
                          </Button>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <Badge variant="outline" className="flex items-center gap-1 px-2 py-1">
                            <Users className="h-3 w-3" />
                            Manager: {manager?.user?.fullName ?? manager?.user?.firstName ?? 'Manager'}
                          </Badge>
                          <Badge variant="outline" className="flex items-center gap-1 px-2 py-1">
                            <Users className="h-3 w-3" />
                            Team Member: {participant?.user?.fullName ?? participant?.user?.firstName ?? 'Team member'}
                          </Badge>
                          {(session.mood ?? session.productivityRating ?? session.teamTempRating) !== undefined && (
                            <>
                            {session.mood !== undefined && (
                              <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
                                <Smile className="h-3 w-3" />
                                Mood: {session.mood}/10
                              </Badge>
                            )}
                            {session.productivityRating !== undefined && (
                              <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
                                <BarChart3 className="h-3 w-3" />
                                Productivity: {session.productivityRating}/10
                              </Badge>
                            )}
                            {session.teamTempRating !== undefined && (
                              <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
                                <Thermometer className="h-3 w-3" />
                                Temperature: {session.teamTempRating}/10
                              </Badge>
                            )}
                            </>
                          )}
                        </div>

                        {session.notes && (
                          <div className="rounded-lg bg-muted/40 p-4 text-sm text-muted-foreground">
                            {session.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          {filteredSessions.length > pageSize && (
            <div className="flex flex-col gap-3 pt-4 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {filteredSessions.length === 0 ? 0 : startIndex + 1}–{endIndex} of {filteredSessions.length} sessions
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrevPage} disabled={safePage === 1}>
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {safePage} of {totalPages}
                </span>
                <Button variant="outline" size="sm" onClick={handleNextPage} disabled={safePage === totalPages}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
